import type { Locale } from "@/lib/i18n/locale";

/**
 * Orders of magnitude quoted on the site, in euros. The cost scenes read them
 * from here; `costs.test.ts` checks that the copy citing them stays in sync.
 */
export const COSTS = {
	businessApp: {
		factor: 5,
		build: { before: 100_000, after: 20_000 },
		yearly: { before: 25_000, after: 5_000 },
	},
	expo: {
		factor: 4,
		yearly: { before: 30_000, after: 7_500 },
		feature: { before: 10_000, after: 2_500 },
	},
	/** The SaaS subscription a custom business app is compared with. */
	saas: { users: 15, monthlyPerUser: 50 },
	/** Hypotheses specific to the use-case pages. */
	useCases: {
		/** Field-service software for a maintenance team. */
		hvac: { users: 25, monthlyPerUser: 50 },
		/** Paid follow-up portal sold by a QHSE consultancy to its clients. */
		qhse: { monthlyPerClient: 150, newClientsPerMonth: 2, months: 12 },
	},
} as const;

export type SaasPlan = { users: number; monthlyPerUser: number };

export const saasYearly = ({ users, monthlyPerUser }: SaasPlan) =>
	users * monthlyPerUser * 12;

/** Year when the subscription has cost as much as the custom software. */
export const breakEvenYear = (plan: SaasPlan) =>
	COSTS.businessApp.build.after /
	(saasYearly(plan) - COSTS.businessApp.yearly.after);

export const SAAS_YEARLY = saasYearly(COSTS.saas);

export const BREAK_EVEN_YEAR = breakEvenYear(COSTS.saas);

/**
 * Cumulative revenue of the QHSE follow-up portal, month by month, when the
 * consultancy signs a steady number of new subscribing clients each month.
 */
export const portalRevenue = () => {
	const { monthlyPerClient, newClientsPerMonth, months } = COSTS.useCases.qhse;
	let total = 0;
	return Array.from({ length: months }, (_, index) => {
		total += (index + 1) * newClientsPerMonth * monthlyPerClient;
		return total;
	});
};

/** First month (1-based) when the portal has paid for the first version. */
export const portalPaybackMonth = () =>
	portalRevenue().findIndex((total) => total >= COSTS.businessApp.build.after) +
	1;

/**
 * Formats whole euros without `Intl`, so the server and every browser render
 * the exact same characters (no hydration mismatch on separators).
 */
export function formatEuro(value: number, locale: Locale) {
	const french = locale.startsWith("fr");
	const digits = String(Math.round(value)).replace(
		/\B(?=(\d{3})+(?!\d))/g,
		french ? " " : ",",
	);
	return french ? `${digits} €` : `€${digits}`;
}

/** Replaces `{key}` placeholders in a dictionary string. */
export function fillTemplate(
	template: string,
	values: Record<string, string | number>,
) {
	return template.replace(/\{(\w+)\}/g, (match, key: string) =>
		key in values ? String(values[key]) : match,
	);
}
